import ServiceUnavailableAlert from 'components/core/ServiceUnavailableAlert';
import { Alert, Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectPatient } from 'redux/features/auth/authSlice';
import history from 'services/history';
import useSWR from 'swr';
import { isInfrastructureError } from 'utils/apiErrors';
import { getAtividadeRoute, laudos } from 'utils/helpers';
import api from '../../services/api';

async function fetchAtividadesPaciente(codPac) {
  const { data } = await api.get(`pacientes/${codPac}/atividades`);

  return (data || []).filter(
    ({ CODPROCEDI, GUIA1 }) =>
      CODPROCEDI !== 1 && CODPROCEDI !== 58 && CODPROCEDI !== 800 && GUIA1 === 'SIM'
  );
}

export default function Atividade() {
  const patient = useSelector(selectPatient);
  const codPac = patient?.codPac;

  const {
    data: atividades = [],
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSWR(
    codPac ? ['atividades-paciente', codPac] : null,
    ([, id]) => fetchAtividadesPaciente(id),
    {
      shouldRetryOnError: true,
      errorRetryCount: 2,
      errorRetryInterval: 3000,
    }
  );

  const temAtividades = atividades.length > 0;
  const exibirErro = Boolean(error) && !isValidating;
  const erroInfraestrutura = isInfrastructureError(error);

  function handleAbrirLaudo(atividade) {
    const codProcedi = Number(atividade?.CODPROCEDI);

    if (laudos.ecg?.includes(codProcedi)) {
      history.push(`/visualizacao-ecg/${atividade.CODLAUDO}`);
      return;
    }

    const route = getAtividadeRoute({
      action: 'visualizar',
      codProcedi,
      codLaudo: atividade?.CODLAUDO,
    });

    if (route) {
      history.push(route);
      return;
    }

    toast.warning('Procedimento não liberado para visualização na web.');
  }

  return (
    <Card className="mt-2">
      <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-3">
        <h5 className="mb-0 fw-semibold">Resultado de Exames</h5>
        <Badge bg="light" text="dark" className="px-3 py-2 fs-6 fw-normal">
          Total: {atividades.length}
        </Badge>
      </Card.Header>

      <Card.Body className="p-0">
        {isLoading && !temAtividades ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" role="status" />
            <p className="mt-2 text-muted small mb-0">Buscando exames...</p>
          </div>
        ) : (
          <>
            {exibirErro &&
              (erroInfraestrutura ? (
                <div className="p-3">
                  <ServiceUnavailableAlert
                    error={error}
                    stale={temAtividades}
                    onRetry={() => mutate()}
                  />
                </div>
              ) : (
                <Alert variant="danger" className="text-center m-3">
                  Erro ao carregar exames do paciente.
                  <div className="mt-2">
                    <Button variant="outline-danger" size="sm" onClick={() => mutate()}>
                      Tentar novamente
                    </Button>
                  </div>
                </Alert>
              ))}

            {temAtividades ? (
              <Table responsive striped hover size="sm" className="align-middle mb-0">
                <thead className="table-light border-bottom">
                  <tr>
                    <th className="ps-3 py-2">Procedimento</th>
                    <th className="py-2">Convênio</th>
                    <th className="py-2">Data</th>
                    <th className="py-2 text-center">Nº Laudo</th>
                  </tr>
                </thead>
                <tbody>
                  {atividades.map((a, index) => (
                    <tr key={`${a.CODLAUDO}-${a.CODPROCEDI}-${index}`}>
                      <td className="ps-3">
                        <Button
                          variant="link"
                          className="p-0 text-decoration-none fw-medium text-primary text-start"
                          onClick={() => handleAbrirLaudo(a)}
                        >
                          {a.DESCPROCEDI}
                        </Button>
                      </td>
                      <td>{a.SIGLACONV}</td>
                      <td>{a.dataFormatada}</td>
                      <td className="text-center">
                        <Badge bg="secondary" className="fw-normal">
                          {a.CODLAUDO || '-'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : !exibirErro ? (
              <div className="p-4">
                <Alert variant="warning" className="text-center mb-0 shadow-sm">
                  Nenhum exame liberado para este paciente no momento.
                </Alert>
              </div>
            ) : null}
          </>
        )}
      </Card.Body>
    </Card>
  );
}
